import { Box } from '@mui/material'
import { CarrierVisitDirection, HoldReaction, OrderResponseDto, UnitType } from '@planning/app/api'
import { HoldEnforcementAlert } from '@planning/components/hold/HoldEnforcementAlert'
import { usePlanningStore } from '@planning/AppProvider'
import { generateEirDataForOrder } from '@planning/pages/GateClerk'
import { orderService } from '@planning/services'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { PrintoutsViewStore } from '@planning/stores/printouts/PrintoutViewStore'
import { mapToInspectContainerFormData } from '@planning/utils/printout-utils'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, EquipmentInterchangeReceipt, PdfFileViewer } from '@tom-ui/ui'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'

export const IncompleteJobsWarningMessageOrder: FC<{
  order: OrderResponseDto
}> = observer(({ order }) => {
  const { t } = useTranslate()

  const isDropOff = order.direction === CarrierVisitDirection.Inbound

  const isContainer = !!order.containerId

  const containerMessage = isDropOff
    ? t('containerDropOff', 'Container drop off')
    : t('containerPickUp', 'Container pick up')
  const nnOrderMessage = isDropOff
    ? t('nnOrderDropOff', 'NN order drop off ref')
    : t('nnOrderPickUp', 'NN order pick up ref')
  const cargoMessage = isDropOff
    ? t('cargoDropOff', 'Cargo drop off ref')
    : t('cargoPickUp', 'Cargo pick up ref')

  let message = cargoMessage
  let ref = order.referenceNumber

  if (isContainer) {
    message = order.containerNumber ? containerMessage : nnOrderMessage
    ref = order.containerNumber ? order.containerNumber : order.referenceNumber
  }

  return (
    <Box>
      {message}: <b>{ref}</b>
    </Box>
  )
})

export const IncompleteJobsWarningMessageBody: FC<{
  orders: OrderResponseDto[]
}> = observer(({ orders }) => {
  const { t } = useTranslate()

  return (
    <>
      {t(
        'truckVisitHasIncompleteJobsWarning',
        'The following orders have incomplete jobs during operations. Check & confirm the following:',
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem 0',
          gap: '.5rem',
        }}
      >
        {orders.map(order => (
          <IncompleteJobsWarningMessageOrder order={order} key={order.id} />
        ))}
      </Box>
    </>
  )
})

export const ManualGateOutWarningDialog: FC<{
  viewStore: GateInViewStore
  holdReaction?: HoldReaction | null
  holdNames?: string[]
}> = observer(({ viewStore, holdReaction, holdNames = [] }) => {
  const { t } = useTranslate()
  const printoutsViewStore = useMemo(() => new PrintoutsViewStore(), [])
  const { appViewStore, dialogStore, orderItemStore, truckVisitItemStore, vesselVisitItemStore } =
    usePlanningStore()

  const fetchLinkedOrderData = async (orderId: number) => {
    const order = _.values(orderItemStore.elements).filter(o => o.id === orderId)[0]
    const linkedOrderId = order && order.data?.linkedOrderId

    let linkedOrder: OrderResponseDto | null = null

    if (linkedOrderId) {
      linkedOrder = await orderService.getById(linkedOrderId)
    }

    return linkedOrder
  }
  async function generateEir(orders: OrderResponseDto[], now: Date) {
    const date = now.toLocaleDateString()

    for (const order of orders) {
      const visit = Object.values(truckVisitItemStore.elements).find(
        tv => tv.id === order.carrierVisitId,
      )

      const inspectOrder = mapToInspectContainerFormData(order)
      const eirData = await generateEirDataForOrder(inspectOrder, now, {
        truckPlate: visit?.truck?.data.licensePlate ?? '',
        truckCompanyName: visit?.company?.data.name,
        driverName: visit?.driver?.data.name,
        driverID: visit?.driver?.data.driverID,
        fetchLinkedOrderData,
        orderItemStore,
        vesselVisitItemStore,
      })

      const doc = <EquipmentInterchangeReceipt t={t} data={eirData} />

      await printoutsViewStore.generatePrintout(
        'Eir',
        doc,
        date,
        order.containerNumber ?? t('unknownContainer', 'Unknown container'),
        order.id,
      )
    }
  }

  async function generateDocuments() {
    try {
      const { selectedVisitWithOrders } = viewStore
      const now = new Date()
      const orders = selectedVisitWithOrders
        .filter(o => o.order?.direction === 'Outbound')
        .map(o => o.order!)

      if (orders.length > 0) {
        printoutsViewStore.setLoading(true)
        await generateEir(orders, now)

        dialogStore.openDialog(
          <PdfFileViewer
            onClose={() => dialogStore.close()}
            files={printoutsViewStore.pdfFileViewerDocuments}
          />,
          true,
        )
      }
    } catch (error) {
      appViewStore.setShowAlert(
        'error',
        t('failedToGeneratePrintouts', 'Failed to generate printous'),
      )
    } finally {
      printoutsViewStore.setLoading(false)
    }
  }

  const openOrders = viewStore.selectedVisitWithOrders
    .filter(o => !!o && !o.order?.isJobFinished)
    .map(o => o.order!)

  const investigationOpenOrders = openOrders
    .filter(o => o.unitType === UnitType.Container && o.direction === CarrierVisitDirection.Inbound)
    .map(o => (o.containerNumber ? o.containerNumber : o.referenceNumber))

  const fulfilledOpenOrders = openOrders
    .filter(
      o => o.unitType !== UnitType.Container || o.direction === CarrierVisitDirection.Outbound,
    )
    .map(o => (o.containerNumber ? o.containerNumber : o.referenceNumber))

  const getWarningMessage = () => {
    if (fulfilledOpenOrders.length > 0 && fulfilledOpenOrders.length === openOrders.length) {
      return t(
        'confirmGateOutMarkOrdersAsFulfilled',
        'Confirming gate out will mark these orders as fulfilled',
      )
    }

    if (
      investigationOpenOrders.length > 0 &&
      investigationOpenOrders.length === openOrders.length
    ) {
      return t(
        'confirmGateOutMarkOrdersWithIssues',
        'Confirming gate out will mark these orders with issues',
      )
    }

    if (investigationOpenOrders.length > 0 && fulfilledOpenOrders.length > 0) {
      return `${t(
        'confirmGateOutMarkOrdersWithIssues',
        'Confirming gate out will mark these orders with issues',
      )}:
      ${investigationOpenOrders.join(', ')}
      ${t(
        'confirmGateOutMarkOrdersAsFulfilled',
        'Confirming gate out will mark these orders as fulfilled',
      )}:
      ${fulfilledOpenOrders.join(', ')}`
    }
  }
  const isBlocked = holdReaction === HoldReaction.Block

  return (
    <ConfirmationDialog
      title={t('incompleteJobsForGateOut', 'Incomplete jobs for gate out')}
      warning={getWarningMessage()}
      warningPosition='bottom'
      open={viewStore.isGateOutWithPendenciesConfirmationDialogOpen}
      onClose={() => viewStore.closeGateOutWithPendenciesConfirmationDialog()}
      onConfirm={async () => {
        viewStore.gateOut()
        await generateDocuments()
      }}
      closeLabel={t('cancel', 'Cancel')}
      primaryActionText={t('confirm', 'Confirm')}
      primaryActionDisabled={isBlocked}
      maxWidth='xs'
    >
      <IncompleteJobsWarningMessageBody orders={openOrders} />
      <Box sx={{ mb: 1 }}>
        <HoldEnforcementAlert reaction={holdReaction ?? null} holdNames={holdNames} />
      </Box>
    </ConfirmationDialog>
  )
})
